// A lesson night wins its date. Pure.
//
// Matt, 2026-09-23: "if there are meals already drawn on those days of the
// week, we should just push them to different days further down the
// calendar, and we can adjust from there." So when the week is planned,
// any Meal Hat row already sitting on a lesson night — a drawn hat meal or
// a hand-placed one-off — slides forward to the next free day: not a
// lesson night, not already taken by another row, and not taken by a row
// moved earlier in the same pass. Rows are moved in date order so the
// displaced meals keep their relative order.
//
// Rows this app placed (`lesson` marker) are never moved; they are the
// nights being planned.

const DAY = 86400000;
const nextISO = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return new Date(d.getTime() + DAY).toISOString().slice(0, 10);
};

/**
 * @param drawnMeals  Meal Hat's `drawnMeals` map (id → row)
 * @param lessonDates ISO dates the lessons will occupy
 * @param meals       Meal Hat's `meals` map, for names
 * @returns [{ rowId, mealId, from, to, name }]
 */
export function planDisplacements (drawnMeals = {}, lessonDates = [], meals = {}) {
  const lessons = new Set(lessonDates);
  const rows = Object.entries(drawnMeals || {})
    .map(([id, row]) => ({ id, ...row }))
    .filter((row) => row && row.assignedDate && !row.lesson);

  const taken = new Set(rows.map((row) => row.assignedDate));
  const moves = [];
  const inTheWay = rows.filter((row) => lessons.has(row.assignedDate)).sort((a, b) => a.assignedDate.localeCompare(b.assignedDate));

  for (const row of inTheWay) {
    let to = nextISO(row.assignedDate);
    while (lessons.has(to) || taken.has(to)) to = nextISO(to);
    taken.add(to);
    moves.push({
      rowId: row.id,
      mealId: row.mealId || null,
      from: row.assignedDate,
      to,
      name: (row.mealId && meals?.[row.mealId]?.name) || row.name || 'a meal'
    });
  }
  return moves;
}

/**
 * The writes for one move, as a multi-path patch on the hat: the row's date,
 * and — for a hat meal — the same date inside the meal's own `drawnDates`
 * (and `lastDrawn`), which is what Meal Hat's draw weighting reads.
 */
export function movePatch (move, meal) {
  const patch = { [`drawnMeals/${move.rowId}/assignedDate`]: move.to };
  if (move.mealId && meal) {
    const dates = Array.isArray(meal.drawnDates) ? meal.drawnDates.map((d) => (d === move.from ? move.to : d)) : null;
    if (dates) patch[`meals/${move.mealId}/drawnDates`] = dates;
    if (meal.lastDrawn === move.from) patch[`meals/${move.mealId}/lastDrawn`] = move.to;
  }
  return patch;
}

export const describeMove = (move, label = (iso) => iso) => `${move.name}: ${label(move.from)} → ${label(move.to)}`;
